import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CryptoJS from "crypto-js";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { useGetAllMemberQuery } from "@/services/memberApi";
import { Check, ChevronDownIcon, ChevronsUpDown, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { isAddress } from "viem";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useGetMembershipsQuery } from "@/services/membershipsApi";
import { Separator } from "@/components/ui/separator";
import { displayDate, formatToPKR, roundToTwoDecimals } from "@/lib/helper";
import { useDebounce } from "@/hooks/use-debounce";
import { ErrorType } from "@/types";
import { SellForm, SellItem } from "@/types/pos/sell";
import { MemberTableDatatypes } from "@/types/member";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { FormProvider, useForm, UseFormSetValue } from "react-hook-form";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";
import { useGetIncomeCategorListQuery } from "@/services/incomeCategoryApi";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import {
  useCreateTransactionMutation,
  useGetTransactionByIdQuery,
  useGetTransactionQuery,
  usePatchTransactionMutation,
  useUpdateTransactionMutation,
} from "@/services/transactionApi";
import MemberForm from "../../members/modal/form";
import Checkout from "./checkout";
import ParkReceipt from "./park-receipt";
import { useGetStaffListQuery } from "@/services/staffsApi";
import { useGetlastRegisterSessionQuery } from "@/services/registerApi";
import useDocumentTitle from "@/components/ui/common/document-title";
import {
  Gender,
  ProductStatus,
  ProductType,
  SaleStatus,
} from "@/types/enums";
import { CustomerCombobox } from "@/components/ui/customer-combobox/customer-combobox";
import { useGetAllProductsQuery } from "@/services/productApi";
import { Product, ProductTypeEnum } from "@/types/system-setting/product";
import { useGetSystemSettingsQuery } from "@/services/systemConfigApi";
const {
  VITE_REGISTER_MAX_HOUR,
  VITE_ENCYRPTION_KEY,
  VITE_SECRET_KEY,
  VITE_BLOCKCHAIN_API_URL,
} = import.meta.env;

interface SearchCriteriaType {
  status: string;
  search_key?: string;
}

const has24HoursPassed = (date: Date) => {
  const now = new Date();
  const differenceInMs = now.getTime() - date.getTime();
  const differenceInHours = differenceInMs / (1000 * 60 * 60);
  return differenceInHours >= VITE_REGISTER_MAX_HOUR;
};

const Sell = () => {
  useDocumentTitle("Sell");
  const { id } = useParams();
  const hide_pos_discount = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_pos_discount ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const navigate = useNavigate();

  const isContinue =
    JSON.parse(localStorage.getItem("isContinue") as string) || null;
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const invoice = useSelector((state: RootState) => state.invoice.invoice);
  const walletTransaction = useSelector(
    (state: RootState) => state.wallet.invoice
  );
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { data: otherSettingsData, refetch: settingsRefetch } = useGetSystemSettingsQuery({
    query: "groups=other_settings"
  })

  const other_settings = useMemo(() => otherSettingsData?.other_settings as Record<string, string>, [otherSettingsData]);

  const counter_number =
    (localStorage.getItem("counter_number") as string) == ""
      ? null
      : Number(localStorage.getItem("counter_number") as string);

  const {
    data: lastSession,
    refetch: lastSessionRefetch,
    isError,
    isFetching,
    error: lastSessionError,
  } = useGetlastRegisterSessionQuery(counter_number as number, {
    refetchOnMountOrArgChange: true,
  });

  // initial value before sale is parked or sold
  const initialValues: SellForm = {
    org_id: orgId,
    counter_id: counter_number as number, //counter id
    batch_id: lastSession?.id as number, //register id
    staff_id: userInfo?.user.id,
    staff_name: userInfo?.user.first_name,
    submitted_to_srb: true,
    member_id: null,
    member_name: null,
    member_email: null,
    member_address: null,
    member_gender: null,
    member_nic: undefined,
    wallet_address: null,
    notes: "",
    refund_details: [
      {
        refund_notes: null,
        created_at: null,
        created_by: null,
      },
    ],
    refund_notes: "",
    receipt_number: other_settings?.invoice_prefix + Math.floor(Math.random() * 99), //will implement with prefix module comes
    tax_number: Math.floor(Math.random() * 99), //from srb
    discount: 0,
    total: 0,
    srb_invoice_id: undefined,
    sub_total: 0,
    tax_amount: 0,
    is_business: false,
    amount_received: 0,
    change_amount: 0,
    status: ProductStatus.Pending,
    transaction_type: SaleStatus.Sale,
    items: [],
    payments: [],
  };

  const form = useForm<SellForm>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    watch,
    register,
    setValue,
    reset,
    formState: { errors },
  } = form;
  const watcher = watch();

  const [productPayload, setProductPayload] = useState<SellItem[]>([]);
  const [showCheckout, setShowCheckout] = useState<boolean>(false);
  const [dayExceeded, setDayExceeded] = useState<boolean>(false);

  useEffect(() => {
    if (invoice) {
      const updatedPayload = {
        ...invoice,
        invoice_id: invoice.id,
        receipt_number: other_settings?.invoice_prefix + Math.floor(Math.random() * 99), //will implement with prefix module comes
        notes: "",
        staff_id: userInfo?.user.id,
        submitted_to_srb: true,
        srb_invoice_id: undefined,
        staff_name: userInfo?.user.first_name,
        counter_id: counter_number as number, //counter id
        batch_id: lastSession?.id as number,
        status: ProductStatus.Pending,
        transaction_type: SaleStatus.Sale,
        tax_number: Math.floor(Math.random() * 99), //from srb
        member_gender: invoice.member_gender as Gender,
        total: Math.round(invoice.total),
        payments: [],
      };
      delete updatedPayload.id;

      // Ensure items have the discount_percentage property for proper calculations
      const itemsWithDiscountPercentage = (invoice.items || []).map((item: any) => {
        let processedItem = {
          ...item,
          discount_percentage: item.discount_percentage || 0, // Ensure this property exists
        };

        // Handle inclusive vs exclusive tax types properly
        if (item.tax_type === "inclusive") {
          // For inclusive tax invoices:
          // - price should be the tax-inclusive price (total)
          // - net_price should be the base price (sub_total)
          // - total should be the tax-inclusive price
          processedItem.price = item.total || item.price; // Use total as the tax-inclusive price
          processedItem.net_price = item.sub_total || item.net_price || item.price; // Base price
        } else {
          // For exclusive tax invoices:
          // - price should be the base price
          // - net_price should be the base price
          // - total should be base price + tax
          processedItem.price = item.price || item.sub_total; // Base price
          processedItem.net_price = item.net_price || item.price || item.sub_total; // Base price
        }

        return processedItem;
      });

      updatedPayload.items = itemsWithDiscountPercentage;
      console.log({ updatedPayload });

      // Set product payload first
      setProductPayload(itemsWithDiscountPercentage as SellItem[]);

      const member = memberListData?.find(
        (member) => member.id == updatedPayload.member_id
      ) as MemberTableDatatypes;
      setCustomer(member);

      // Reset the form with the updated payload
      reset(updatedPayload, {
        keepIsSubmitted: false, // Reset the form's submission state
        keepSubmitCount: false, // Reset the count of submissions
        keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
        keepDefaultValues: true, // Keep default values as provided by initialValue
      });

      // For inclusive tax invoices, we need to use the original invoice values
      // to avoid recalculation issues
      if (invoice.items?.some((item: any) => item.tax_type === "inclusive")) {
        // Set form values directly from invoice for inclusive tax
        setValue("sub_total", Math.round(invoice.sub_total));
        setValue("tax_amount", Math.round(invoice.tax_amount));
        setValue("total", Math.round(invoice.total));
        setValue("discount", invoice.discount || 0);

        // Update the payload to match invoice values
        const updatedItems = itemsWithDiscountPercentage.map((item: any) => ({
          ...item,
          sub_total: item.price * item.quantity, // Use base price * quantity
          tax_amount: Math.round(invoice.tax_amount), // Use invoice tax amount
          total: item.price, // For inclusive, total equals the tax-inclusive price
        }));

        setProductPayload(updatedItems as SellItem[]);
        setValue("items", updatedItems);
      } else {
        // For exclusive tax, run normal calculations
        // Trigger calculation after state has been set
        setTimeout(() => {
          updateFormValues(itemsWithDiscountPercentage as SellItem[], { clampDiscount: true });
        }, 0);
      }

      setShowCheckout(true);
    }
  }, [invoice]);


  useEffect(() => {
    console.log("Wallet Transaction:", walletTransaction);

    if (walletTransaction) {
      const updatedPayload = {
        ...walletTransaction,
        receipt_number: other_settings?.invoice_prefix + Math.floor(Math.random() * 99),
        notes: walletTransaction.notes || "",
        staff_id: userInfo?.user.id,
        staff_name: userInfo?.user.first_name,
        counter_id: counter_number as number,
        batch_id: lastSession?.id as number,
        status: ProductStatus.Pending,
        transaction_type:
          (walletTransaction.transaction_type as SaleStatus) ||
          SaleStatus.Refund,
        tax_number: Math.floor(Math.random() * 99),
        member_gender: walletTransaction.member_gender || Gender.Male,
        // Ensure these values are properly set
        total: Number(walletTransaction.total) || 0,
        sub_total: Number(walletTransaction.sub_total) || 0,
        tax_amount: Number(walletTransaction.tax_amount) || 0,
        discount: Number(walletTransaction.discount) || 0,
        srb_invoice_id: undefined,
        payments: [],
      };

      console.log("Updated Payload:", updatedPayload);

      // Ensure items have the discount_percentage property for proper calculations
      const itemsWithDiscountPercentage = (walletTransaction.items || []).map((item: any) => ({
        ...item,
        discount_percentage: item.discount_percentage || 0, // Ensure this property exists
        net_price: item.net_price || item.price, // Initialize net_price if not present
      }));

      updatedPayload.items = itemsWithDiscountPercentage;

      // Set product payload first
      setProductPayload(itemsWithDiscountPercentage as SellItem[]);

      // Find and set customer
      const member = memberListData?.find(
        (member) => member.id === updatedPayload.member_id
      ) as MemberTableDatatypes;
      if (member) {
        setCustomer(member);
      }

      // Reset form with new values - make sure all required fields are included
      reset(updatedPayload, {
        keepIsSubmitted: false,
        keepSubmitCount: false,
        keepDirtyValues: false,
        keepDefaultValues: true,
      });

      // Trigger calculation after state has been set
      setTimeout(() => {
        updateFormValues(itemsWithDiscountPercentage as SellItem[], { clampDiscount: true });
      }, 0);

      setShowCheckout(true);
    }
  }, [walletTransaction]);

  // search product
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteriaType>({
    status: "active",
  });
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [maxProductsQauntity, setMaxProductsQauntity] = useState<
    Record<number, number>
  >({});
  const debouncedInputValue = useDebounce(inputValue, 500);
  // select customer
  const [customer, setCustomer] = useState<MemberTableDatatypes | null>(null);
  const [openParkSale, setParkSale] = useState(false);
  const [openStaffDropdown, setStaffDropdown] = useState(false);
  const [action, setAction] = useState<"add" | "edit" | "member-created">(
    "add"
  );
  const [openMemberForm, setOpenMemberForm] = useState<boolean>(false);
  const [editMember, setEditMember] = useState<MemberTableDatatypes | null>(
    null
  );

  // listing apis
  const { data: staffList } = useGetStaffListQuery(orgId);
  const { data: orgTaxType } = useGetOrgTaxTypeQuery(orgId) || {
    tax_type: "exclusive",
  };
  const { data: incomeCatData } = useGetIncomeCategorListQuery(orgId);
  const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);
  const { data: memberhsipList } = useGetMembershipsQuery({
    org_id: orgId,
    query: query,
  });
  const { data: productList } = useGetAllProductsQuery({
    org_id: orgId,
    query: query,
  });
  const { data: memberList, refetch: memberRefetch } = useGetAllMemberQuery({
    org_id: orgId,
    query: "sort_key=id&sort_order=desc",
  });
  const [createTransaction] = useCreateTransactionMutation();
  const [updateTransaction] = useUpdateTransactionMutation();
  const [transactionId, setTransactionId] = useState<number | undefined>(
    id ? Number(id) : undefined
  );
  const { data: retriveSaleData, refetch: retriveSaleRefetch } =
    useGetTransactionByIdQuery(
      {
        transaction_id: transactionId as number,
        // counter_id: watcher.counter_id,
      },
      {
        skip: transactionId == undefined,
      }
    );

  // get unpaid sales to retrive sale
  const { data: transactionData, refetch: transactionRefetch } =
    useGetTransactionQuery(
      {
        counter_id: counter_number as number,
        query: `status=Pending&sort_order=desc`,
      },
      {
        skip: counter_number == null,
      }
    );

  const transactiontableData = useMemo(() => {
    return Array.isArray(transactionData?.data) ? transactionData?.data : [];
  }, [transactionData]);

  const memberListData = useMemo(() => {
    return Array.isArray(memberList?.data) ? memberList?.data : [];
  }, [memberList]);

  const memberhsipListData = useMemo(() => {
    return Array.isArray(memberhsipList?.data) ? memberhsipList?.data : [];
  }, [memberhsipList]);

  const productListData = useMemo(() => {
    return Array.isArray(productList?.data) ? productList?.data : [];
  }, [productList]);

  // if navigating back to sell page, the checkout screen must reset
  useEffect(() => {
    if (!id && !invoice && !walletTransaction) {
      reset(initialValues as SellForm);
      setCustomer(null);
      setProductPayload([]);
      setShowCheckout(false);
      setIsDiscountManuallySet(false);
    }
    if (lastSession) {
      setValue("batch_id", lastSession.id as number);
    }
  }, [id, lastSession, invoice]);

  // for past 24 hour validation
  useEffect(() => {
    if (counter_number !== null) {
      lastSessionRefetch();
    }

    if (isError) {
      const errorCode =
        typeof lastSessionError === "object" &&
        "status" in lastSessionError &&
        (lastSessionError as ErrorType).status;

      if (errorCode == 404) {
        toast({
          variant: "destructive",
          title: "No active register session found.",
        });
        navigate(`/admin/pos/register`);
        return;
      }
    } else if (!isFetching && lastSession?.closing_time !== null) {
      toast({
        variant: "destructive",
        title: "No active register session found.",
      });
      navigate(`/admin/pos/register`);
      return;
    } else {
      const opening_time = new Date(lastSession?.opening_time as string);
      //
      if (has24HoursPassed(opening_time) && !isContinue) {
        setDayExceeded(true);
      } else {
        setDayExceeded(false);
      }
    }
  }, [lastSession, isError, lastSessionError, lastSessionRefetch]);

  // for search products
  useEffect(() => {
    setSearchCriteria((prev) => {
      const newCriteria = { ...prev };
      if (debouncedInputValue.trim() !== "") {
        newCriteria.search_key = debouncedInputValue;
      } else {
        delete newCriteria.search_key;
      }
      return newCriteria;
    });
  }, [debouncedInputValue, setSearchCriteria]);

  // for filters to add in query params
  useEffect(() => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchCriteria)) {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            params.append(key, val); // Append each array element as a separate query parameter
          });
        } else {
          params.append(key, value); // For non-array values
        }
      }
    }
    const newQuery = params.toString();
    setQuery(newQuery);
  }, [searchCriteria]);

  function handleOpenForm() {
    setAction("add");
    setEditMember(null);
    setOpenMemberForm(true);
  }
  const normalizeProducts = (products: Product[]) =>
    products.map((product) => ({
      ...product,
      name: product.product_name, // Normalize product_name to name
      net_price: product.price, // Normalize price to net_price
    }));
  // product lists by category
  const productCategories = [
    {
      type: ProductType.Membership,
      label: "Memberships",
      products: memberhsipListData,
      empty: "No Memberships found",
    },
    {
      type: ProductType.Event,
      label: "Events",
      products: [],
      empty: "No Events found",
    },
    {
      type: ProductType.Product,
      label: "Products",
      products: normalizeProducts(productListData),
      // products: [],
      empty: "No Products found",
    },
  ];

  const updateFormValues = (
    updatedPayload: typeof productPayload,
    options?: { clampDiscount?: boolean }
  ) => {
    // Safety check: ensure we have valid data
    if (!updatedPayload || !Array.isArray(updatedPayload) || updatedPayload.length === 0) {
      return;
    }

    // Calculate totals from raw data
    let rawSubtotal = 0;
    let rawProductDiscount = 0;

    // First pass: calculate subtotals and product discounts
    const itemsWithBasicCalcs = updatedPayload.map((prod) => {
      // For inclusive tax, the price field contains the tax-inclusive price
      // We need to extract the base price for calculations
      let basePrice = prod.price;
      if (prod.tax_type === "inclusive" && prod.tax_rate > 0) {
        // Extract base price from tax-inclusive price
        basePrice = prod.price / (1 + prod.tax_rate / 100);
      }

      const itemSubtotal = basePrice * prod.quantity;
      rawSubtotal += itemSubtotal;

      const discountPercentage = prod.discount_percentage || 0;
      const itemDiscount = (itemSubtotal * discountPercentage) / 100;
      rawProductDiscount += itemDiscount;

      return {
        ...prod,
        itemSubtotal,
        itemDiscount,
        discountPercentage,
        basePrice, // Store the extracted base price
      };
    });

    // Determine manual discount
    const shouldClamp = options?.clampDiscount ?? true;
    const currentManualDiscount = discountAmt || 0;
    const minAllowedDiscount = Math.round(rawProductDiscount);
    const maxAllowedDiscount = Math.max(0, Math.round(rawSubtotal) - 1);
    const clampedManualDiscount = Math.min(
      Math.max(currentManualDiscount, minAllowedDiscount),
      maxAllowedDiscount
    );
    const effectiveManualDiscount = shouldClamp
      ? clampedManualDiscount
      : currentManualDiscount;

    if (shouldClamp && clampedManualDiscount !== currentManualDiscount) {
      setValue("discount", clampedManualDiscount, { shouldValidate: false });
    }

    // Console logging for debugging
    console.log("=== SUBTOTAL CALCULATION DEBUG ===");
    console.log("Raw Subtotal (sum of all item prices * quantities):", rawSubtotal);
    console.log("Raw Product Discount (sum of all product discounts):", rawProductDiscount);
    console.log("Current Manual Discount:", currentManualDiscount);
    console.log("Effective Manual Discount:", effectiveManualDiscount);
    console.log("Min Allowed Discount (product discount total):", minAllowedDiscount);

    // Check if manual discount is actually overridden (not just matching product discount)
    const isManualDiscountOverride = isDiscountManuallySet && effectiveManualDiscount > minAllowedDiscount;

    console.log("Is Manual Discount Override:", isManualDiscountOverride);

    let rawTotalTax = 0;
    let rawTotalAfterDiscounts = 0;

    // Second pass: calculate tax after ALL discounts (product + manual)
    const updatedItemsWithCalculations = itemsWithBasicCalcs.map((prod) => {
      const itemSubtotal = prod.itemSubtotal;
      const itemProductDiscount = prod.itemDiscount;

      // Calculate proportional share based on ORIGINAL price ratio
      const itemProportionalShare = rawSubtotal > 0
        ? itemSubtotal / rawSubtotal
        : 0;

      // Allocate the ENTIRE manual discount proportionally
      const itemAllocatedManualDiscount = effectiveManualDiscount * itemProportionalShare;

      // Determine the actual discount to apply
      const totalItemDiscount = isManualDiscountOverride
        ? itemAllocatedManualDiscount
        : itemProductDiscount;

      // Amount after ALL discounts (this is the base price after discounts)
      const discountedAmount = itemSubtotal - totalItemDiscount;

      // Calculate tax based on tax type
      let itemTax = 0;
      let itemTotal = 0;
      let netPrice = 0;

      if (prod.tax_rate > 0) {
        if (prod.tax_type === "inclusive") {
          // For tax-inclusive: tax is already included in the original price
          // The discountedAmount is the base price after discounts
          // We need to add tax back to get the final tax-inclusive total
          itemTax = discountedAmount * (prod.tax_rate / 100);
          itemTotal = discountedAmount + itemTax; // Total is base price + tax
          netPrice = discountedAmount; // Net price is the base amount (after discounts, before tax)
        } else if (prod.tax_type === "exclusive") {
          // For tax-exclusive: tax is added on top
          itemTax = discountedAmount * (prod.tax_rate / 100);
          itemTotal = discountedAmount + itemTax; // Total is price + tax
          netPrice = discountedAmount; // Net price is the base amount
        }
      } else {
        // No tax
        itemTax = 0;
        itemTotal = discountedAmount;
        netPrice = discountedAmount;
      }

      rawTotalTax += itemTax;
      rawTotalAfterDiscounts += itemTotal;

      // Console logging for each item
      console.log(`Item ${prod.description}:`, {
        itemSubtotal,
        itemProductDiscount,
        itemProportionalShare: (itemProportionalShare * 100).toFixed(2) + "%",
        itemAllocatedManualDiscount,
        isManualDiscountOverride,
        totalItemDiscount,
        discountedAmount,
        itemTax,
        itemTotal,
        netPrice,
        taxType: prod.tax_type
      });

      return {
        ...prod,
        sub_total: itemSubtotal, // Keep original subtotal for display
        discount: isManualDiscountOverride ? itemAllocatedManualDiscount : itemProductDiscount,
        tax_amount: itemTax,
        total: itemTotal,
        net_price: netPrice, // This should be the amount after discounts but before tax for exclusive, or after discounts and tax extraction for inclusive
      };
    });

    // Calculate grand total based on tax type
    const isRefund = watcher.transaction_type === SaleStatus.Refund;
    let grandTotal = 0;

    if (isRefund) {
      // For refunds, use the simpler calculation
      grandTotal = rawTotalAfterDiscounts;
    } else {
      // For regular sales, the total should be the sum of all item totals
      // rawTotalAfterDiscounts already contains the correct tax-inclusive totals
      grandTotal = rawTotalAfterDiscounts;
    }

    // For inclusive tax, ensure the total matches what was in the original invoice
    // The grandTotal should be the tax-inclusive total
    const hasInclusiveTax = updatedPayload.some(item => item.tax_type === "inclusive");
    if (hasInclusiveTax) {
      // For inclusive tax, the grandTotal should match the original invoice total
      // But since we're recalculating, we need to ensure it matches
      console.log("Inclusive tax detected - ensuring totals match original invoice");
    }

    console.log("Final Calculations:", {
      rawSubtotal,
      rawTotalTax,
      rawTotalAfterDiscounts,
      effectiveManualDiscount,
      grandTotal: Math.round(grandTotal)
    });
    console.log("=== END SUBTOTAL CALCULATION DEBUG ===");

    // Round and set values
    setValue("sub_total", Math.round(rawSubtotal));
    setValue("tax_amount", Math.round(rawTotalTax));
    setValue("total", Math.round(grandTotal));
    setValue("items", updatedItemsWithCalculations);

    setProductPayload(updatedItemsWithCalculations);
  };

  const [discountError, setDiscountError] = useState("");
  const [isDiscountManuallySet, setIsDiscountManuallySet] = useState(false);

  const totalPrice = watcher.sub_total; // Assuming subtotal is the total price

  const validateDiscount = (value: number) => {
    // Calculate the minimum discount (product discounts)
    const productDiscountTotal = productPayload.reduce((total, product) => {
      const discountPercentage = product.discount_percentage || 0;
      const itemSubtotal = product.price * product.quantity;
      const itemDiscount = (itemSubtotal * discountPercentage) / 100;
      return total + itemDiscount;
    }, 0);

    // User cannot go below the product discount amount
    if (value < productDiscountTotal) {
      setDiscountError(`Discount cannot be less than ${Math.round(productDiscountTotal)} (product discount)`);
      return false;
    }

    // User cannot be more than or equal to subtotal
    if (value >= (totalPrice ?? 0)) {
      setDiscountError("Discount cannot be greater than or equal to subtotal");
      return false;
    }

    setDiscountError("");
    return true;
  };

  const addProduct = (product: any, type: string) => {
    // Check if this is any wallet product
    const isWalletProduct =
      type === ProductType.Product &&
      product.type === ProductTypeEnum.wallet;

    if (isWalletProduct) {
      // Calculate current total of all wallet products in cart
      const currentWalletTotal = productPayload.reduce((total, item) => {
        const isExistingWalletProduct =
          item.item_type === ProductType.Product &&
          productListData.find((p) => p.id === item.item_id)?.type === ProductTypeEnum.wallet;

        return isExistingWalletProduct
          ? total + (item.price * item.quantity) // Use price * quantity for validation as this is before tax
          : total;
      }, 0);

      // Check if adding this product would exceed the limit
      if (currentWalletTotal + product.net_price > 100000) {
        toast({
          variant: "destructive",
          title: "Wallet limit exceeded",
          description: "Total wallet amount cannot exceed 1 lac (100,000)"
        });
        return; // Don't add the product if it would exceed the limit
      }

      // Individual product price check (keep existing check)
      if (product.net_price > 100000) {
        toast({
          variant: "destructive",
          title: "Price limit exceeded",
          description: "Wallet amount cannot exceed 1 lac (100,000)"
        });
        product.net_price = 100000;
      }
    }

    console.log({ product }, "product-addProduct")
    // Reset discount error when adding products
    setDiscountError("");

    // Reset manual discount flag when adding products
    setIsDiscountManuallySet(false);

    // Rest of your original function remains the same
    const isFlexiblePrice =
      type === ProductType.Product &&
      product.type === ProductTypeEnum.wallet &&
      product.flexible_price;

    // Continue with your existing logic
    const incomeCat = incomeCatData?.find(
      (item) => item.id == product.income_category_id
    );
    const saleTax = salesTaxData?.find(
      (item) => item.id == incomeCat?.sale_tax_id
    );
    const taxRate = Number(product.tax_perc);
    console.log("taxRate-addProduct", taxRate);
    // For flexible price products, we'll allow editing the price later
    const productPrice = isFlexiblePrice ? 0 : product.net_price;

    // Calculate discount (store raw values, no rounding)
    const discountPercentage = isFlexiblePrice ? 0 : product.discount || 0;
    const discountAmount = isFlexiblePrice
      ? 0
      : (product.net_price * discountPercentage) / 100;

    // Store raw values without rounding - updateFormValues will handle the calculation
    const subTotal = product.net_price; // Raw price without rounding
    const taxAmount = 0; // Will be calculated in updateFormValues
    const total = 0; // Will be calculated in updateFormValues

    // Construct the new product payload
    const newProductPayload = {
      item_id: product.id,
      item_type: type,
      description: product.name,
      is_srb: product.is_srb,
      revenue_type: product.revenue_type,
      quantity: 1,
      price: productPrice,
      tax_rate: taxRate,
      discount: discountAmount, // Per-item discount amount
      discount_percentage: discountPercentage, // Store the percentage for recalculation
      sub_total: subTotal, // Will be calculated in updateFormValues
      tax_type: orgTaxType?.tax_type as string,
      tax_name: saleTax?.name as string,
      income_category_id: product.income_category_id as number,
      sale_tax_id: saleTax?.id as number,
      total: total, // Will be calculated in updateFormValues
      tax_amount: taxAmount, // Will be calculated in updateFormValues
      net_price: productPrice, // Initial net_price, will be recalculated in updateFormValues
    };

    // Update the product payload to ensure unique entries and recalculate totals
    setProductPayload((prevPayload) => {
      const existingProduct = prevPayload.find(
        (prod) => prod.item_id == product.id
      );

      let updatedPayload;
      if (existingProduct) {
        // Update existing product in payload - only update quantity, let updateFormValues handle calculations
        updatedPayload = prevPayload.map((prod) =>
          prod.item_id == product.id
            ? {
              ...prod,
              quantity: prod.quantity + 1,
              // Keep other values as they are, updateFormValues will recalculate
            }
            : prod
        );
      } else {
        // Add new product to payload
        updatedPayload = [...prevPayload, newProductPayload];
      }

      updateFormValues(updatedPayload, { clampDiscount: true });

      return updatedPayload;
    });
  };

  const updateProductQuantity = (
    productId: number,
    action: "increment" | "decrement"
  ) => {
    setProductPayload((prevPayload) => {
      // Find the product before processing
      const currentProduct = prevPayload.find(prod => prod.item_id === productId);

      // Check if this is a wallet product and if incrementing would exceed 1 lac
      if (action === "increment" && currentProduct) {
        const isWalletProduct =
          currentProduct.item_type === ProductType.Product &&
          productListData.find((p) => p.id === currentProduct.item_id)?.type === ProductTypeEnum.wallet;

        if (isWalletProduct) {
          // Check individual product total (using base price for validation)
          const singleProductNewTotal = currentProduct.price * (currentProduct.quantity + 1);

          // Calculate total of ALL wallet products (excluding the current one)
          const otherWalletProductsTotal = prevPayload.reduce((total, item) => {
            if (item.item_id === currentProduct.item_id) return total; // Skip current product

            const isWalletProduct =
              item.item_type === ProductType.Product &&
              productListData.find((p) => p.id === item.item_id)?.type === ProductTypeEnum.wallet;

            return isWalletProduct ? total + (item.price * item.quantity) : total; // Use price * quantity for validation
          }, 0);

          // Check combined total
          if (singleProductNewTotal + otherWalletProductsTotal > 100000) {
            toast({
              variant: "destructive",
              title: "Wallet limit exceeded",
              description: "Total wallet amount across all wallet products cannot exceed 1 lac (100,000)"
            });
            return prevPayload; // Return unchanged if limit would be exceeded
          }

          // Keep the individual product check as well
          if (singleProductNewTotal > 100000) {
            toast({
              variant: "destructive",
              title: "Quantity limit reached",
              description: "Total wallet amount cannot exceed 1 lac (100,000)"
            });
            return prevPayload;
          }
        }
      }

      // Original implementation continues
      const newPayload = prevPayload
        .map((product) => {
          if (product.item_id === productId) {
            // New quantity based on action
            const newQuantity =
              action === "increment"
                ? product.quantity + 1
                : product.quantity - 1;

            // Remove product if quantity reaches zero
            if (newQuantity <= 0) return null;

            // Only update quantity, let updateFormValues handle all calculations
            return {
              ...product,
              quantity: newQuantity,
              // Keep other values as they are, updateFormValues will recalculate everything
            };
          }
          return product;
        })
        .filter((product) => product !== null) as typeof prevPayload;

      // If decrementing, adjust the overall manual discount by the item's per-unit product discount
      if (action === "decrement") {
        const decrementedProduct = prevPayload.find((p) => p.item_id === productId);
        if (decrementedProduct) {
          const perUnitSubtotal = decrementedProduct.price; // price reflects per-unit base
          const perUnitProductDiscount = ((decrementedProduct.discount_percentage || 0) * perUnitSubtotal) / 100;

          // Compute new product-discount total and subtotal based on the newPayload
          const recalculatedSubtotal = newPayload.reduce((sum, it) => sum + (it.price * it.quantity), 0);
          const recalculatedProductDiscount = newPayload.reduce((sum, it) => {
            const perc = it.discount_percentage || 0;
            return sum + ((it.price * it.quantity) * perc) / 100;
          }, 0);

          const minAllowed = Math.round(recalculatedProductDiscount);
          const maxAllowed = Math.max(0, Math.round(recalculatedSubtotal) - 1);
          const currentManual = Number(watcher.discount || 0);

          // Reduce current manual discount by the removed unit's product discount
          const proposed = currentManual - Math.round(perUnitProductDiscount);
          const clamped = Math.min(Math.max(proposed, minAllowed), maxAllowed);
          if (clamped !== currentManual) {
            setValue("discount", clamped, { shouldValidate: false });
          }
        }
      }

      // Reset values if payload is empty
      if (newPayload.length === 0) {
        setValue("discount", 0);
        setValue("sub_total", 0);
        setValue("tax_amount", 0);
        setValue("total", 0);
        setIsDiscountManuallySet(false);
      }

      updateFormValues(newPayload, { clampDiscount: true });
      setDiscountError("");

      return newPayload;
    });
  };

  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Add this function to fetch and display token balance when a customer is selected
  const fetchAndDisplayBalance = async (
    walletAddress: string | null | undefined
  ) => {
    if (!walletAddress) {
      setTokenBalance(null);
      return;
    }

    try {
      setLoadingBalance(true);
      const balanceData = await getTokenBalance({
        userAddress: walletAddress,
        orgId: orgId,
      });
      setTokenBalance(balanceData.balance);
    } catch (error) {
      console.error("Failed to retrieve token balance:", error);
      setTokenBalance(null);
      toast({
        variant: "destructive",
        title: "Failed to retrieve token balance",
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoadingBalance(false);
    }
  };
  const discountAmt = watch("discount") || 0;

  useEffect(() => {
    if (discountAmt >= 0 && productPayload.length > 0) {
      // Only trigger recalculation if we have items in the payload
      // This prevents conflicts when loading invoice/wallet data
      updateFormValues(productPayload, { clampDiscount: false });
    }

    if (customer) {
      setValue("is_business", customer.is_business as boolean);
      setValue("member_id", customer.id);
      setValue("member_name", customer.first_name + " " + customer.last_name);
      setValue("member_email", customer.email);
      setValue("member_address", customer.address_1);
      setValue("wallet_address", customer.wallet_address);
      setValue("member_gender", customer.gender);
      setValue("member_nic", customer.nic);

      // Fetch token balance when customer is selected and has a wallet address
      if (customer.wallet_address) {
        fetchAndDisplayBalance(customer?.wallet_address);
      } else {
        setTokenBalance(null);
      }
    } else {
      setTokenBalance(null);
    }
  }, [customer, discountAmt, isDiscountManuallySet]);

  // Additional effect to handle discount validation
  useEffect(() => {
    if (discountAmt !== undefined && productPayload.length > 0 ) {
      // Validate without toasting on each keystroke to avoid input jitter
      validateDiscount(discountAmt);
    }
  }, [discountAmt, productPayload]);

  // Track when discount field is manually changed vs automatically set
  useEffect(() => {
    if (productPayload.length > 0) {
      const productDiscountTotal = productPayload.reduce((total, product) => {
        const discountPercentage = product.discount_percentage || 0;
        const itemSubtotal = product.price * product.quantity;
        return total + (itemSubtotal * discountPercentage) / 100;
      }, 0);

      // If discount field matches product discount total (within tolerance), it was likely set automatically
      // If it's significantly higher, it was likely manually increased
      const tolerance = 0.01;
      if (discountAmt > (Math.round(productDiscountTotal) + tolerance)) {
        setIsDiscountManuallySet(true);
      } else if (discountAmt < (Math.round(productDiscountTotal) - tolerance)) {
        // If discount is lower than product discount, it might be manually decreased
        setIsDiscountManuallySet(true);
      } else {
        // If discount matches product discount (within tolerance), reset manual flag
        setIsDiscountManuallySet(false);
      }
    }
  }, [discountAmt, productPayload]);

  const handleCloseDayExceeded = () => {
    setDayExceeded(false);
    navigate(`/admin/pos/register`);
  };

  const handleContinueDayExceeded = () => {
    localStorage.setItem("isContinue", "true");
    setDayExceeded(false);
  };

  // const handleGetWallet = async () => {
  //   try {
  //     const wallet = await getWallet(orgId); // Replace 1 with the actual ID
  //     console.log("Wallet Data:", wallet);
  //   } catch (error) {
  //     console.error("Error fetching wallet:", error);
  //   }
  // };

  const validateFlexiblePricing = () => {
    // Check if any flexible price product has a price of 0
    const invalidFlexibleProducts = productPayload.filter(
      (product) =>
        product.item_type === ProductType.Product &&
        productListData.find((p) => p.id === product.item_id)?.flexible_price &&
        (product.price === 0 || !product.price)
    );

    if (invalidFlexibleProducts.length > 0) {
      toast({
        variant: "destructive",
        title: "Price required",
        description: `Please enter a price for ${invalidFlexibleProducts.map((p) => p.description).join(", ")} before proceeding to checkout.`,
      });
      return false;
    }

    return true;
  };

  const validateCheckout = () => {
    if (!customer) {
      toast({
        variant: "destructive",
        title: "Please select a customer",
      });
      return false;
    }

    // Add this line to validate flexible pricing
    if (!validateFlexiblePricing()) {
      return false;
    }
    // Check if there's a wallet product in the cart but customer has no wallet address
    const hasWalletProduct = productPayload.some(
      (product) =>
        product.item_type === ProductType.Product &&
        productListData.find((p) => p.id === product.item_id)?.type ===
        ProductTypeEnum.wallet
    );

    if (
      hasWalletProduct &&
      (!customer?.wallet_address || customer.wallet_address === null)
    ) {
      toast({
        variant: "destructive",
        title: "Cannot top up wallet as member has no wallet",
      });
      return false;
    }

    if (hasWalletProduct) {
      // First check if wallet address exists
      if (!customer?.wallet_address || customer.wallet_address === null) {
        toast({
          variant: "destructive",
          title: "Cannot top up wallet as member has no wallet",
        });
        return false;
      }

      // Then validate if it's a proper Ethereum address
      const isValidEthAddress = /^0x[a-fA-F0-9]{40}$/.test(
        customer.wallet_address
      );
      const isValidAddress = isAddress(customer.wallet_address, {
        strict: true,
      });
      if (!isValidEthAddress) {
        toast({
          variant: "destructive",
          description: "Customer's wallet address is not valid",
        });
        return false;
      }
    }

    if (productPayload.length == 0) {
      toast({
        variant: "destructive",
        title:
          "Please add at least one item to the receipt to enable checkout.",
      });
      return false;
    }

    // For refunds, discount validation is different since discount is applied at item level
    if (watcher.transaction_type !== SaleStatus.Refund && (watcher.discount as number) >= (watcher.sub_total as number)) {
      toast({
        variant: "destructive",
        title: "Discount should be less than subtotal amount",
      });
      return false;
    }
    setDiscountError("");
    return true;
  };

  const paymentCheckout = () => {
    const validation = validateCheckout();
    validation && setShowCheckout(true);
    setDiscountError("");
  };

  const parkSale = async () => {
    const validation = validateCheckout();
    if(!validation) {
      return;
    }
    const payload = { ...watcher, status: ProductStatus.Pending, submitted_to_srb: false };
    try {
      if (!payload.id) {
        const resp = await createTransaction(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Sale parked successfully",
          });
        }
      } else if (payload.id) {
        const resp = await updateTransaction(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Sale parked updated successfully",
          });
        }
      }

      // Clear ALL state FIRST before resetting form
      setProductPayload([]);
      setDiscountError("");
      setIsDiscountManuallySet(false);
      setCustomer(null);
      setTransactionId(undefined);
      
      // Use setTimeout to ensure state updates are processed before reset
      setTimeout(() => {
        reset(initialValues as SellForm);
        transactionRefetch();

        if (id) {
          navigate("/admin/pos/sell/");
        }
      }, 0);
    } catch (error: unknown) {
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || typedError.data?.message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  useEffect(() => {
    if (Number(id)) {
      setTransactionId(Number(id));
      setDiscountError("");
    }
  }, [id]);

  useEffect(() => {
    // for retrive sale, payment from sale history (pending to paid), refund
    if (retriveSaleData) {
      console.log({ retriveSaleData }, "inside hrere");
      // transactionId is for refund (remove tax in refund)

      let refundItemsWithoutTax = retriveSaleData.items;

      if (Number(id)) {
        refundItemsWithoutTax = retriveSaleData.items.map((item) => ({
          ...item,
          tax_amount: 0,
          tax_rate: 0,
          // Preserve the original discount values for proper calculation
          discount: item.discount || 0,
          discount_percentage: item.discount_percentage || 0,
        }));
      }

      const payload = {
        ...retriveSaleData,
        staff_id: userInfo?.user.id,
        counter_id: counter_number as number, //counter id
        staff_name: userInfo?.user.first_name,
        batch_id: lastSession?.id, //register id
        items: refundItemsWithoutTax, //items without tax
      };

      // for refund
      if (Number(id) && payload.status === "Paid") {
        delete payload.id;
        payload.transaction_type = SaleStatus.Refund;
        payload.amount_received = 0;
        payload.change_amount = 0;
        payload.receipt_number = other_settings?.invoice_prefix + Math.floor(Math.random() * 99);
        payload.main_transaction_id = Number(id);
        payload.payments = [];

        const initialMaxQuantities = payload.items.reduce(
          (acc, item) => {
            acc[item.item_id] = item.quantity;
            return acc;
          },
          {} as Record<number, number>
        );
        setMaxProductsQauntity(initialMaxQuantities);
      }
      // Ensure items have the discount_percentage property
      let itemsWithDiscountPercentage = refundItemsWithoutTax
      if (Number(id)) {
        itemsWithDiscountPercentage = refundItemsWithoutTax.map((item: any) => {
          let processedItem = {
            ...item,
            discount_percentage: item.discount || 0,
          };

          // Handle inclusive vs exclusive tax types properly for refunds/retrievals
          if (item.tax_type === "inclusive") {
            // For inclusive tax: use total as tax-inclusive price
            processedItem.price = item.total || item.price;
            processedItem.net_price = item.sub_total || item.net_price || item.price;
          } else {
            // For exclusive tax: use price as base price
            processedItem.price = item.price || item.sub_total;
            processedItem.net_price = item.net_price || item.price || item.sub_total;
          }

          return processedItem;
        });
      }

      // Update the payload with normalized items
      payload.items = itemsWithDiscountPercentage;

      // Recompute subtotal and product-discount minimum from items
      // For inclusive tax, we need to extract base prices for calculations
      const recalculatedSubtotal = itemsWithDiscountPercentage.reduce((sum, it: any) => {
        let basePrice = it.price;
        if (it.tax_type === "inclusive" && it.tax_rate > 0) {
          basePrice = it.price / (1 + it.tax_rate / 100);
        }
        return sum + (basePrice * it.quantity);
      }, 0);

      const recalculatedProductDiscount = itemsWithDiscountPercentage.reduce((sum, it: any) => {
        const perc = it.discount_percentage || 0;
        let basePrice = it.price;
        if (it.tax_type === "inclusive" && it.tax_rate > 0) {
          basePrice = it.price / (1 + it.tax_rate / 100);
        }
        return sum + ((basePrice * it.quantity) * perc) / 100;
      }, 0);

      const minAllowedDiscount = Math.round(recalculatedProductDiscount);
      const maxAllowedDiscount = Math.max(0, Math.round(recalculatedSubtotal) - 1);
      const currentManual = Number(retriveSaleData.discount || 0);
      const clampedManual = Math.min(Math.max(currentManual, minAllowedDiscount), maxAllowedDiscount);

      // For inclusive tax, use the original values to avoid calculation issues
      if (retriveSaleData.items?.some((item: any) => item.tax_type === "inclusive")) {
        payload.sub_total = Math.round(Number(retriveSaleData.sub_total));
        payload.tax_amount = Math.round(Number(retriveSaleData.tax_amount));
        payload.total = Math.round(retriveSaleData.total);
      } else {
        payload.sub_total = Math.round(recalculatedSubtotal);
        payload.total = Math.round(retriveSaleData.total); // Keep original total
      }
      payload.discount = clampedManual;


      console.log("refund", { payload });
      reset(payload, { keepDirtyValues: true });

      setCustomer(
        memberListData?.find(
          (member) => member.id == payload.member_id
        ) as MemberTableDatatypes
      );

      setProductPayload(itemsWithDiscountPercentage as SellItem[]);

      // Trigger calculation after state has been set
      setTimeout(() => {
        updateFormValues(itemsWithDiscountPercentage as SellItem[], { clampDiscount: true });
      }, 0);
      setTimeout(() => {
        updateFormValues(itemsWithDiscountPercentage as SellItem[], { clampDiscount: true });
      }, 0);

      // open checkout if status is set to refunded
      if (Number(id) && payload.transaction_type === SaleStatus.Refund) {
        setShowCheckout(true);
      }
    }
  }, [retriveSaleData]);

  console.log({ watcher, errors, productPayload, transactionId });


  const handleFlexiblePriceChange = (
    productId: number,
    newPriceStr: string
  ) => {
    // Handle empty input separately
    if (newPriceStr === "") {
      // Existing empty input handling code
      return;
    }

    const newPrice = parseFloat(newPriceStr);
    if (isNaN(newPrice) || newPrice < 0) return;

    // Check if price exceeds 1 lac (100,000)
    if (newPrice > 100000) {
      toast({
        variant: "destructive",
        title: "Price limit exceeded",
        description: "Wallet amount cannot exceed 1 lac (100,000)"
      });

      // IMPORTANT: Reset the input to the max allowed value instead of returning
      setProductPayload((prevPayload) => {
        const newPayload = prevPayload.map((product) => {
          if (product.item_id === productId) {
            // Use maximum allowed value instead
            const maxAllowedPrice = 100000;

            // Just update the price, let updateFormValues handle all calculations
            return {
              ...product,
              price: maxAllowedPrice,
              // Keep other values as they are, updateFormValues will recalculate everything
            };
          }
          return product;
        });

        updateFormValues(newPayload);
        return newPayload;
      });
      return;
    }

    // Existing code for normal price updates
    setProductPayload((prevPayload) => {
      const newPayload = prevPayload.map((product) => {
        if (product.item_id === productId) {
          // Just update the price, let updateFormValues handle all calculations
          return {
            ...product,
            price: newPrice,
            // Keep other values as they are, updateFormValues will recalculate everything
          };
        }
        return product;
      });

      updateFormValues(newPayload, { clampDiscount: true });
      return newPayload;
    });
  };
  return (
    <div>
      <FormProvider {...form}>
        <div className="p-5">
          {!showCheckout && (
            <Card className=" h-fit px-3 py-4 max-w-[1100px] mx-auto">
              <div className="grid grid-cols-1  slg:grid-cols-2 justify-start items-start gap-3">
                <div className="min-h-36  p-2">
                  <FloatingLabelInput
                    id="search"
                    placeholder="Search"
                    onChange={(event) => setInputValue(event.target.value)}
                    className="w-full pl-8 text-gray-400 rounded-sm"
                    icon={
                      <Search className="size-4 text-gray-400 absolute  z-10 left-2" />
                    }
                  />

                  <Tabs
                    defaultValue={ProductType.Membership}
                    className="w-full mt-4 "
                  >
                    <TabsList variant="underline">
                      {productCategories.map((category) => (
                        <TabsTrigger
                          key={category.type}
                          value={category.type}
                          variant="underline"
                        >
                          {category.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {productCategories.map((category) => (
                      <TabsContent
                        className="m-0  w-full "
                        key={category.type}
                        value={category.type}
                      >
                        {category.products.length > 0 ? (
                          <div className="mt-4 w-full flex flex-wrap gap-4 justify-center items-center">
                            {category.products.map(
                              (product: Record<string, any>, i) => (
                                <button
                                  key={i}
                                  disabled={
                                    invoice ||
                                      (id &&
                                        watcher.transaction_type ==
                                        SaleStatus.Refund)
                                      ? true
                                      : false
                                  }
                                  onClick={() =>
                                    addProduct(product, category?.type)
                                  }
                                  className={`relative group ${!watcher.id || (id && watcher.transaction_type == SaleStatus.Refund && "hover:bg-primary/20 hover:text-black/60 cursor-pointer")} size-28 text-sm  flex flex-col gap-2 bg-primary/30 justify-center items-center p-2 rounded-sm `}
                                >
                                  <span className="capitalize">
                                    {product.name}
                                  </span>
                                  {category.type === ProductType.Product &&
                                    product.type === ProductTypeEnum.wallet &&
                                    product.flexible_price ? (
                                    <span className="text-blue-600 font-medium">
                                      Flexible Price
                                    </span>
                                  ) : (
                                    <span>
                                      {formatToPKR(product.net_price)}
                                    </span>
                                  )}
                                  {category.type === ProductType.Product &&
                                    product.type === ProductTypeEnum.wallet ? (
                                    <div className="shadow-lg rounded-sm px-2">
                                      <h2 className="text-sm font-bold text-gray-800">
                                        Wallet
                                      </h2>
                                    </div>
                                  ) : category.type === ProductType.Product ? (
                                    <div className="shadow-lg rounded-sm px-2">
                                      <h2 className="text-sm font-bold text-gray-800">
                                        Product
                                      </h2>
                                    </div>
                                  ) : null}
                                  {!watcher.id ||
                                    (id &&
                                      watcher.transaction_type ==
                                      SaleStatus.Refund && (
                                        <span className="absolute invisible group-hover:visible  bottom-0   text-black/80 text-sm z-20 p-1">
                                          Add
                                        </span>
                                      ))}
                                </button>
                              )
                            )}
                          </div>
                        ) : (
                          <p className="col-span-2 text-sm text-center w-full p-2 mt-2">
                            {category.empty}
                          </p>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>

                <div className="h-full flex flex-col justify-start space-y-2 rounded-sm bg-gray-100 p-2">
                  <div className="item-center flex gap-3">
                    <RetriveSaleCombobox
                      label={"Retrive Sale"}
                      list={transactiontableData}
                      setCustomer={setCustomer}
                      customer={customer}
                      customerList={memberListData}
                      setTransactionId={setTransactionId}
                      setWatcherValue={setValue}
                      setProductPayload={setProductPayload}
                      refetch={retriveSaleRefetch}
                      reset={reset}
                    />
                    <Button
                      onClick={() => {
                        const validation = validateCheckout();
                        validation && setParkSale(true);
                      }}
                      disabled={
                        id && watcher.transaction_type == SaleStatus.Refund
                          ? true
                          : false
                      }
                      className="w-full justify-center items-center gap-2"
                    >
                      <i className="fa-regular fa-clock"></i>
                      Park Sale
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1">
                      <CustomerCombobox
                        label={"customer"}
                        className="bg-white"
                        customerList={memberListData}
                        setCustomer={setCustomer}
                        customer={customer}
                        disable={
                          (id &&
                            watcher.transaction_type == SaleStatus.Refund) ||
                            watcher.id ||
                            id
                            ? true
                            : false
                        }
                      />
                    </div>
                    <Button
                      onClick={handleOpenForm}
                      className=" text-white justify-center items-center gap-2"
                    >
                      <i className="fa-regular fa-plus"></i>
                      <span className="text-nowrap">Add Member</span>
                    </Button>
                  </div>
                  {/* {customer && (
                    <div className="text-xs mt-1 flex items-center">
                      {loadingBalance ? (
                        <span className="text-gray-500">
                          Loading balance...
                        </span>
                      ) : tokenBalance !== null ? (
                        <div className=" text-blue-700 px-2 py-1 rounded-sm font-normal w-full text-end">
                          Wallet Balance : {tokenBalance}
                        </div>
                      ) : customer.wallet_address ? (
                        <span className="text-gray-500">
                          Unable to load balance
                        </span>
                      ) : (
                        <span className="text-gray-500">No wallet address</span>
                      )}
                    </div>
                  )} */}
                  <div className="bg-white/90">
                    {productPayload.map((product) => (
                      <>
                        <div className="flex justify-between items-center gap-2 p-2">
                          <div>
                            <p className="capitalize">{product.description}</p>
                            {product.discount > 0 && (
                              <p className="line-through text-sm text-gray-500 text-left">
                                {formatToPKR(product.sub_total)}
                              </p>
                            )}

                          </div>
                          <div className="flex flex-col items-center">
                            {product.item_type === ProductType.Product &&
                              productListData.find(
                                (p) => p.id === product.item_id
                              )?.flexible_price ? (
                              <div className="flex flex-col space-y-1 justify-end">
                                <div className="flex flex-row items-center gap-2">

                                  <label
                                    htmlFor={`flex-price-${product.item_id}`}
                                    className="text-xs text-gray-600 ml-auto"
                                  >
                                    Enter price:
                                  </label>
                                  <FloatingLabelInput
                                    type="number"
                                    id={`flex-price-${product.item_id}`}
                                    value={product.price || ""}
                                    required={true}
                                    placeholder="Price"
                                    min="0"
                                    max="100000"
                                    className="w-24 text-black rounded-sm h-7 py-0 px-1 ml-auto"
                                    onChange={(e) =>
                                      handleFlexiblePriceChange(
                                        product.item_id,
                                        e.target.value
                                      )
                                    }
                                  />
                                </div>
                                <p className="text-sm mt-1 ml-auto">
                                  {product.price > 0 ? (
                                    <>
                                      Total: {formatToPKR(product.total)}
                                      <span className="text-xs">
                                        {" "}
                                        ({product.tax_rate}%)
                                      </span>
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </p>
                              </div>
                            ) : (
                              <p className="text-sm">
                                {formatToPKR(
                                  product.sub_total - product.discount
                                )}
                                <span className="text-xs">
                                  {" "}
                                  ({product.tax_rate}%)
                                </span>
                              </p>
                            )}
                            <div className="inline-flex items-center ml-auto ">
                              {/* Quantity controls - keeping your existing code */}
                              <button
                                disabled={invoice ? true : false}
                                onClick={() => {
                                  updateProductQuantity(
                                    product.item_id,
                                    "decrement"
                                  );
                                }}
                                className="disabled:cursor-not-allowed disabled:hover:bg-transparent bg-white rounded-l border text-gray-600 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 inline-flex items-center px-2 py-1 border-r border-gray-200"
                              >
                                <i className="fa fa-minus"></i>
                              </button>
                              <div className="border-t border-b border-gray-100 text-gray-600 hover:bg-gray-100 inline-flex items-center px-4 py-0 select-none">
                                {product.quantity}
                              </div>
                              <button
                                disabled={
                                  invoice ||
                                    (id &&
                                      watcher.transaction_type ==
                                      SaleStatus.Refund &&
                                      product.quantity ==
                                      maxProductsQauntity[product.item_id])
                                    ? true
                                    : false
                                }
                                onClick={() =>
                                  updateProductQuantity(
                                    product.item_id,
                                    "increment"
                                  )
                                }
                                className="disabled:cursor-not-allowed disabled:hover:bg-transparent bg-white rounded-l border text-gray-600 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 inline-flex items-center px-2 py-1 border-r border-gray-200"
                              >
                                <i className="fa fa-plus"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                        <Separator className="h-[1px] font-thin rounded-full" />
                      </>
                    ))}
                  </div>

                  <div className="rounded-sm bg-white/90 mx-1">
                    <div className="space-y-2 px-2 bg-gray-100 pt-2 ">
                      {/* Tax Type Indicator */}
                      {/* {productPayload.length > 0 && productPayload[0].tax_type && (
                        <div className="w-full flex gap-2 items-center justify-between pb-2 border-b border-gray-200">
                          <p className="text-sm font-medium">Tax Type</p>
                          <span className={`text-xs font-medium px-2 py-1 rounded ${
                            productPayload[0].tax_type === "inclusive" 
                              ? "bg-blue-100 text-blue-700" 
                              : "bg-green-100 text-green-700"
                          }`}>
                            {productPayload[0].tax_type === "inclusive" ? "Inclusive" : "Exclusive"}
                          </span>
                        </div>
                      )} */}
                      {/* Net total before discount (before name was subtotal) */}
                      <div className="w-full flex gap-2  items-center justify-between">
                        <p>Net price</p>
                        <p>{formatToPKR(watcher.sub_total)}</p>{" "}
                        {/* Display Subtotal */}
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between">
                        <p>Discount</p>
                        <div className="flex flex-col items-end">
                          <FloatingLabelInput
                            type="number"
                            id="discount"
                            defaultValue={watcher.discount}
                            step="1"
                            min={0}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            onKeyDown={(e) => {
                              if (e.key === "." || e.key === ",") {
                                e.preventDefault();
                              }
                              // Prevent arrow keys from exceeding bounds
                              const productDiscountTotal = productPayload.reduce((total, product) => {
                                const percentage = product.discount_percentage || 0;
                                const subtotal = product.price * product.quantity;
                                return total + (subtotal * percentage) / 100;
                              }, 0);
                              const minAllowed = Math.round(productDiscountTotal);
                              const maxAllowed = Math.max(0, Math.round(watcher.sub_total || 0) - 1);
                              const current = Number((e.currentTarget as HTMLInputElement).value) || 0;
                              if ((e.key === "ArrowUp" && current >= maxAllowed) || (e.key === "ArrowDown" && current <= minAllowed)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={
                              (Number(id) &&
                                watcher.transaction_type == SaleStatus.Refund) ||
                              hide_pos_discount == "no_access" ||
                              productPayload.length == 0
                            }
                            placeholder="Enter discount amount"
                            className="w-fit text-black rounded-sm text-right"
                            {...register("discount", {
                              setValueAs: (value) => {
                                // Handle empty string or invalid input
                                if (value === "" || value === null || value === undefined) {
                                  return 0;
                                }
                                const numValue = Number(value);
                                return isNaN(numValue) ? 0 : numValue;
                              },
                              min: 0,
                              validate: (value) => validateDiscount(value ?? 0),
                              onBlur: (e) => {
                                const inputValue = (e?.target as HTMLInputElement)?.value;

                                // If field is empty, set to minimum allowed (product discount)
                                if (inputValue === "" || inputValue === null || inputValue === undefined) {
                                  const productDiscountTotal = productPayload.reduce((total, product) => {
                                    const percentage = product.discount_percentage || 0;
                                    const subtotal = product.price * product.quantity;
                                    return total + (subtotal * percentage) / 100;
                                  }, 0);
                                  const minAllowed = Math.round(productDiscountTotal);
                                  setValue("discount", minAllowed, { shouldValidate: false });
                                  setIsDiscountManuallySet(false);
                                  return;
                                }

                                const productDiscountTotal = productPayload.reduce((total, product) => {
                                  const percentage = product.discount_percentage || 0;
                                  const subtotal = product.price * product.quantity;
                                  return total + (subtotal * percentage) / 100;
                                }, 0);
                                const minAllowed = Math.round(productDiscountTotal);
                                const maxAllowed = Math.max(0, Math.round(watcher.sub_total || 0) - 1);
                                const entered = Number(inputValue) || 0;
                                const clamped = Math.min(Math.max(entered, minAllowed), maxAllowed);
                                if (clamped !== entered) {
                                  setValue("discount", clamped, { shouldValidate: false });
                                  // If clamped value matches product discount, reset manual flag
                                  if (Math.abs(clamped - minAllowed) <= 0.01) {
                                    setIsDiscountManuallySet(false);
                                  }
                                }
                              },
                            })}
                          />
                          {discountError && (
                            <p className="text-xs text-red-500 mt-1 text-right max-w-[200px]">{discountError}</p>
                          )}
                        </div>
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between">
                        {/* newly showing, the net price after discount */}
                        <p>Subtotal</p>
                        <p>{formatToPKR(Number(watcher.sub_total) - (Number(watcher.discount) ?? 0))}</p>
                      </div>

                      <div className="w-full flex gap-2 items-center justify-between">
                        <p>
                          Tax {productPayload.length > 0 && productPayload[0].tax_type === "inclusive" && (
                            <span className="text-xs text-gray-500">(included)</span>
                          )}
                        </p>
                        <p>{formatToPKR(watcher.tax_amount)}</p>{" "}
                        {/* Display Tax */}
                      </div>


                      <div className="w-full flex gap-2 items-center justify-between font-bold">
                        <p>Total</p>
                        <p>
                          {formatToPKR(
                            (id && watcher.transaction_type == SaleStatus.Refund
                              ? -Number(watcher.total)
                              : watcher.total) as number
                          )}
                        </p>{" "}
                        {/* Display Total */}
                      </div>
                      <Separator className="my-2" />
                      <div className="w-full flex gap-2 items-center justify-between font-bold">
                        <p>Processed By</p>
                        <div>
                          <Popover
                            open={openStaffDropdown}
                            onOpenChange={setStaffDropdown}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="
                                
                                    w-[200px]
                                
                                font-normal capitalize  hover:bg-transparent border-[1px] shadow-sm bg-white "
                                aria-expanded={openStaffDropdown}
                              >
                                <span className="w-full text-left font-normal">
                                  {watcher.staff_id
                                    ? staffList?.find(
                                      (staff) =>
                                        staff.value == watcher.staff_id
                                    )?.label
                                    : "Select Staff"}
                                </span>
                                <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="p-0 relative "
                              side="bottom"
                            >
                              <Command className="w-full ">
                                <CommandList>
                                  <CommandInput
                                    placeholder="Select Staff"
                                    className="font-normal"
                                  />
                                  <CommandEmpty>
                                    No Staff matched the Search.
                                  </CommandEmpty>
                                  <CommandGroup className="">
                                    {staffList &&
                                      staffList.map((staff: any) => (
                                        <CommandItem
                                          value={staff.label}
                                          key={staff.value}
                                          className="font-normal"
                                          onSelect={() => {
                                            setValue(
                                              "staff_id",
                                              Number(staff.value)
                                            );
                                            setValue("staff_name", staff.label);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              staff.value == watcher.staff_id
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          {staff.label}
                                        </CommandItem>
                                      ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <Button
                        className="w-full bg-primary text-black rounded-sm"
                        onClick={paymentCheckout}
                        disabled={discountError !== ""}
                      >
                        {id && watcher.transaction_type == SaleStatus.Refund
                          ? "Refund"
                          : "Checkout"}
                      </Button>

                      {/* <Button
                        className="w-full bg-primary text-black rounded-sm"
                        onClick={handleGetWallet}
                      >
                        Get wallet Address
                      </Button> */}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {showCheckout && (
            <Checkout
              initialValues={initialValues}
              setShowCheckout={setShowCheckout}
              productPayload={productPayload}
              setCustomer={setCustomer}
              setProductPayload={setProductPayload}
              customer={customer}
              watcher={watcher}
            />
          )}

          <DayExceeded
            isOpen={dayExceeded}
            onClose={handleCloseDayExceeded}
            onContinue={handleContinueDayExceeded}
            closeModal={() => setDayExceeded(false)}
          />
        </div>

        <ParkReceipt
          isOpen={openParkSale}
          onClose={() => setParkSale(false)}
          parkSale={parkSale}
        />
      </FormProvider>

      <MemberForm
        open={openMemberForm}
        setOpen={setOpenMemberForm}
        memberData={editMember}
        setMemberData={setEditMember}
        action={action}
        setAction={setAction}
        refetch={memberRefetch}
        breadcrumb="POS"
      />
    </div>
  );
};

export default Sell;

interface DayExceededProps {
  isOpen: boolean;
  onClose: any;
  onContinue: any;
  closeModal: any;
}

export function DayExceeded({
  isOpen,
  onClose,
  onContinue,
  closeModal,
}: DayExceededProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={closeModal}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Register is Open more than 24 Hours
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            Do you want to continue with this register session or close to
            create a new session?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>Close</AlertDialogCancel>
          <AlertDialogCancel
            className="bg-primary text-black border-transparent hover:"
            onClick={onContinue}
          >
            Continue
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface ComboboxTypes {
  list?: SellForm[];
  customerList?: MemberTableDatatypes[];
  setCustomer: any;
  setTransactionId?: any;
  customer: any;
  label?: string;
  disable?: boolean;
  setWatcherValue: UseFormSetValue<SellForm>;
  setProductPayload: React.Dispatch<React.SetStateAction<SellItem[]>>;
  refetch?: any;
  reset?: any;
}

export function RetriveSaleCombobox({
  list,
  setCustomer,
  customer,
  label,
  customerList,
  setTransactionId,
  setWatcherValue,
  setProductPayload,
  refetch,
  reset,
}: ComboboxTypes) {
  const modifiedList = list?.map((item: any) => ({
    ...item,
    transactionId: item.id,
    value: item.member_id,
    label: item.member_name,
  }));
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="disabled:cursor-not-allowed capitalize w-full justify-center items-center gap-2  bg-white rounded-sm border-[1px]"
        >
          <i className="fa fa-share"></i>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className=" min-w-[25rem] p-0" side="bottom">
        <Command>
          <CommandList className="">
            <CommandEmpty>No customer found.</CommandEmpty>
            {modifiedList && modifiedList.length > 0 && (
              <>
                <div className="flex justify-between gap-3 px-3 py-1 items-center font-semibold">
                  <p>Items</p>
                  <p>Customer</p>
                </div>
                <Separator className="mx-2 w-96" />
              </>
            )}
            <CommandGroup className="">
              {modifiedList?.map((modCustomer) => (
                <CommandItem
                  key={modCustomer.transactionId + ""}
                  value={modCustomer.transactionId + ""}
                  onSelect={(currentValue) => {
                    // find member_id from transaction list
                    const transaction = modifiedList.find(
                      (item) => item.transactionId == currentValue
                    );

                    console.log(
                      {
                        member: transaction.member_id,
                        tranactionId: currentValue,
                        stateTransaction: value,
                      },
                      currentValue == value
                    );
                    setValue(currentValue);
                    const customer = customerList?.find(
                      (item: any) => item.id == transaction.member_id
                    );
                    setCustomer(customer);
                    setTransactionId(currentValue);
                    setProductPayload(transaction.items);
                    reset(transaction);
                    setOpen(false);
                  }}
                  className="cursor-pointer flex flex-col items-start"
                >
                  <div className="flex gap-3 text-xs  justify-between items-center w-full">
                    <div>
                      <div className="flex gap-1">
                        {modCustomer.items
                          .slice(0, 1)
                          .map((list: SellItem, index: number) => (
                            <div key={index}>
                              <span>{list.quantity}</span> x{" "}
                              <span className="capitalize">
                                {list.description}
                              </span>
                            </div>
                          ))}

                        {modCustomer.items.length > 1 && (
                          <div className="text-gray-500">
                            +{modCustomer.items.length - 1} more
                          </div>
                        )}
                      </div>
                      <p className="text-xs">
                        Parked by {displayDate(modCustomer.transaction_date)},
                        by{" "}
                        <span className="capitalize">
                          {modCustomer.staff_name}
                        </span>
                      </p>
                    </div>
                    <p className="capitalize"> {modCustomer.label} </p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const encryptSecretKey = (secretKey: string) => {
  // Convert the secretKey string to Base64
  return btoa(secretKey);
};

export const getWallet = async (id: number) => {
  try {
    // const encryptedSecretKey = encryptSecretKey(VITE_SECRET_KEY);

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY
    );
    const response = await fetch(
      `${VITE_BLOCKCHAIN_API_URL}/get-wallet?id=${id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-secret-key": encryptedSecretKey,
        },
      }
    );
    console.log("encription secrey", encryptedSecretKey);
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to get wallet:", error);
    throw error;
  }
};

export const mintToken = async (data: {
  user_id: number;
  transaction_id: number;
  invoice_id: number;
  jwt_token: string;
  org_id: number;
  address: string;
  amount: number;
  payment_method: string;
  product_name: string;
  product_description: string;
}) => {
  try {
    const {
      user_id,
      transaction_id,
      invoice_id,
      jwt_token,
      org_id,
      address,
      amount,
      payment_method,
      product_name,
      product_description,
    } = data;

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY
    );

    const response = await fetch(`${VITE_BLOCKCHAIN_API_URL}/mint-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-secret-key": encryptedSecretKey,
      },

      body: JSON.stringify({
        user_id,
        transaction_id,
        invoice_id,
        jwt_token,
        org_id,
        address,
        amount,
        payment_method,
        product_name,
        product_description,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to mint token");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to mint token:", error);
    throw error;
  }
};

export const burnToken = async (data: {
  org_id: number;
  address: string;
  amount: number;
}) => {
  try {
    const {
      org_id,
      address,
      amount,
    } = data;

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY
    );

    const response = await fetch(`${VITE_BLOCKCHAIN_API_URL}/burn-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-secret-key": encryptedSecretKey,
      },
      body: JSON.stringify({
        org_id,
        address_from: address,
        amount,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to burn token");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to burn token:", error);
    throw error;
  }
};


export const getTokenBalance = async (data: {
  userAddress: string;
  orgId: number;
}) => {
  try {
    const { userAddress, orgId } = data;

    const encryptedSecretKey = encryptSecretKey(
      import.meta.env.VITE_SECRET_KEY
    );

    const response = await fetch(
      `${VITE_BLOCKCHAIN_API_URL}/balance/${userAddress}/${orgId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-secret-key": encryptedSecretKey,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to retrieve token balance");
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to retrieve token balance:", error);
    throw error;
  }
};